/**
 * TappableText – wraps each word of a phrase in a subtle tappable span so the
 * learner can look up meanings without leaving the session.
 *
 * Tap → `onWordTap(word, context)` is called; the parent typically opens a
 * <WordDefinitionSheet>. We:
 *   - Preserve punctuation attached to tokens (e.g. "Hola," → word "Hola",
 *     glued ",").
 *   - Ignore single-character tokens (punctuation-only noise).
 *   - Underline on hover so it looks interactive without being shouty.
 */
export default function TappableText({ text, onWordTap, className = '', context = '' }) {
  if (!text) return null;
  // Split into tokens while keeping separators so we can rejoin faithfully.
  const parts = text.split(/(\s+)/);
  return (
    <span className={className}>
      {parts.map((tok, i) => {
        if (/^\s+$/.test(tok)) return <span key={`ws-${i}`}>{tok}</span>;
        // Peel off leading/trailing punctuation so "Hola," → word="Hola", tail=","
        const m = tok.match(/^([^\p{L}\p{N}'’-]*)([\p{L}\p{N}'’-]+(?:-[\p{L}\p{N}'’-]+)*)([^\p{L}\p{N}'’-]*)$/u);
        if (!m) return <span key={`p-${i}`}>{tok}</span>;
        const [, lead, word, tail] = m;
        return (
          // Token position + word combined → stable across re-renders even
          // when the same word appears multiple times in a sentence.
          <span key={`${i}-${word}`}>
            {lead}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onWordTap?.(word, context || text); }}
              className="border-b border-dashed border-[var(--lf-border)] transition-colors hover:border-[var(--lf-primary)] hover:text-[var(--lf-primary)]"
              data-testid={`tap-word-${word.toLowerCase()}`}
            >
              {word}
            </button>
            {tail}
          </span>
        );
      })}
    </span>
  );
}
